import React, { useState, useEffect } from 'react';
import api from '../../../api/axios';
import toast from 'react-hot-toast';
import Select from 'react-select';
import InteractivePlot from './InteractivePlot';
import { Box, Paper, Typography, Button, Chip, TextField, MenuItem, Select as MuiSelect, FormControl, InputLabel } from '@mui/material';

const MissingImputation = () => {
  const [step, setStep] = useState(0);
  const [datasets, setDatasets] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [selectedDatasetId, setSelectedDatasetId] = useState(null);
  const [columns, setColumns] = useState([]);
  const [savedParams, setSavedParams] = useState({});
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Analysis state variables
  const [savedAnalysisId, setSavedAnalysisId] = useState(null);
  const [analysisStatus, setAnalysisStatus] = useState('draft');
  const [analysisDescription, setAnalysisDescription] = useState('');
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  const [isCommentsExpanded, setIsCommentsExpanded] = useState(true);
  const [updatingMetadata, setUpdatingMetadata] = useState(false);
  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false);
  
  // AI Assistant state
  const [aiRequest, setAiRequest] = useState('');
  const [aiPlan, setAiPlan] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiExecuting, setAiExecuting] = useState(false);
  const [showAI, setShowAI] = useState(false);
  
  // Mode detection
  const mode = new URLSearchParams(window.location.search).get('mode');
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit' || !mode;

  const [chartTitle, setChartTitle] = useState(() => localStorage.getItem('mi_chart_title') || 'Missing Imputation Analysis');
  const [chartXLabel, setChartXLabel] = useState(() => localStorage.getItem('mi_chart_xlabel') || 'Category');
  const [chartYLabel, setChartYLabel] = useState(() => localStorage.getItem('mi_chart_ylabel') || 'Target Variable');
  const [showChartEditor, setShowChartEditor] = useState(false);

  const [formData, setFormData] = useState({
    Default_Date: null, risk_driver: null, Target_variable: null, Imputation_method: null,
    constant_value: '', mean_median_mode: 'Mean', n_neighbors: 2, weights: 'uniform', new_column_name: ''
  });

  const columnOptions = columns.map(c => ({ value: c, label: c }));
  const methodOptions = [
    { value: 'Remove rows', label: 'Remove rows' },
    { value: 'Constant', label: 'Constant' },
    { value: 'Mean/Median/Mode', label: 'Mean/Median/Mode' },
    { value: 'KNN', label: 'KNN' }
  ];

  // Load saved analysis on mount
  useEffect(() => {
    const analysisId = new URLSearchParams(window.location.search).get('analysis_id');
    if (analysisId && !isNaN(parseInt(analysisId))) {
      loadSavedAnalysis(parseInt(analysisId));
    } else {
      fetchDatasets();
    }
  }, []);

  const loadSavedAnalysis = async (analysisId) => {
    setIsLoadingAnalysis(true);
    try {
      const res = await api.get(`/analyses/${analysisId}`);
      const analysis = res.data;
      
      if (analysis.status) setAnalysisStatus(analysis.status);
      if (analysis.description) setAnalysisDescription(analysis.description);
      setSavedAnalysisId(analysis.id);
      if (analysis.comments && Array.isArray(analysis.comments)) setComments(analysis.comments);
      
      if (analysis.input_dataset) {
        const datasetInfo = analysis.input_dataset;
        setSelectedDataset({
          value: datasetInfo.dataset_id,
          label: datasetInfo.dataset_name,
          rows: datasetInfo.rows,
          columns: datasetInfo.columns
        });
        setSelectedDatasetId(datasetInfo.dataset_id);
      }
      
      if (analysis.input_parameters && analysis.input_parameters.columns) {
        setColumns(analysis.input_parameters.columns);
      }
      
      if (analysis.input_parameters && analysis.input_parameters.formData) {
        const savedFormData = analysis.input_parameters.formData;
        setFormData({
          ...formData,
          Default_Date: savedFormData.Default_Date ? { value: savedFormData.Default_Date, label: savedFormData.Default_Date } : null,
          risk_driver: savedFormData.risk_driver ? { value: savedFormData.risk_driver, label: savedFormData.risk_driver } : null,
          Target_variable: savedFormData.Target_variable ? { value: savedFormData.Target_variable, label: savedFormData.Target_variable } : null,
          Imputation_method: savedFormData.Imputation_method ? { value: savedFormData.Imputation_method, label: savedFormData.Imputation_method } : null,
          constant_value: savedFormData.constant_value || '',
          mean_median_mode: savedFormData.mean_median_mode || 'Mean',
          n_neighbors: savedFormData.n_neighbors || 2,
          weights: savedFormData.weights || 'uniform'
        });
        setSavedParams(analysis.input_parameters.savedParams || {});
      }
      
      if (analysis.output_result) {
        setResults(analysis.output_result);
        setStep(2);
        toast.success('Analysis loaded with results');
      } else if (analysis.input_parameters) {
        setStep(1);
        toast.success('Analysis loaded. Please run the imputation to see results.');
      }
    } catch (err) {
      toast.error('Failed to load analysis');
      fetchDatasets();
    } finally {
      setIsLoadingAnalysis(false);
    }
  };

  const fetchDatasets = async () => {
    try {
      const projectId = new URLSearchParams(window.location.search).get('project_id');
      if (!projectId) {
        setDatasets([]);
        return;
      }
      const res = await api.get(`/api/v1/tables/project/${projectId}/datasets`);
      const datasets = res.data.map(ds => ({
        id: ds.id,
        name: ds.original_filename,
        size_mb: (ds.file_size / 1e6).toFixed(2),
        rows: ds.row_count,
        columns: ds.column_count,
        exists: true,
        value: ds.id,
        label: `${ds.original_filename} (${ds.row_count?.toLocaleString()} rows)`
      }));
      setDatasets(datasets);
    } catch (err) {
      toast.error('Failed to load datasets');
    }
  };

  const handleSelectDataset = async () => {
    if (!selectedDataset) return;
    setLoading(true);
    try {
      const res = await api.post(`/api/missing-imputation/select-dataset?dataset_id=${selectedDataset.value}`);
      setColumns(res.data.columns || []);
      setSelectedDatasetId(selectedDataset.value);
      toast.success(`Loaded: ${res.data.shape[0].toLocaleString()} rows`);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const handleSaveParameters = async () => {
    if (!formData.risk_driver || !formData.Target_variable || !formData.Imputation_method) {
      toast.error('Please fill all required fields'); 
      return;
    }
    setLoading(true);
    try {
      await api.post('/api/missing-imputation/step0', {
        Default_Date: formData.Default_Date?.value || '',
        risk_driver: formData.risk_driver.value,
        Target_variable: formData.Target_variable.value,
        Imputation_method: formData.Imputation_method.value
      });
      setSavedParams({
        Default_Date: formData.Default_Date?.value || '',
        risk_driver: formData.risk_driver.label,
        Target_variable: formData.Target_variable.label,
        Imputation_method: formData.Imputation_method.label
      });
      toast.success('Parameters saved'); 
      setStep(1);
    } catch { toast.error('Failed to save'); }
    finally { setLoading(false); }
  };

  const handleRunImputation = async () => {
    setLoading(true);
    try {
      const payload = {};
      if (formData.Imputation_method?.value === 'Constant') payload.constant_value = formData.constant_value;
      else if (formData.Imputation_method?.value === 'Mean/Median/Mode') payload.mean_median_mode = formData.mean_median_mode;
      else if (formData.Imputation_method?.value === 'KNN') { 
        payload.n_neighbors = formData.n_neighbors; 
        payload.weights = formData.weights; 
      }
      
      const res = await api.post('/api/missing-imputation/step1', payload);
      setResults(res.data.results); 
      toast.success(res.data.message); 
      setStep(2);
    } catch { toast.error('Imputation failed'); }
    finally { setLoading(false); }
  };

  const handleSaveColumn = async () => {
    if (!formData.new_column_name) { toast.error('Enter a column name'); return; }
    setSaving(true);
    try { 
      await api.post('/api/missing-imputation/step2', { new_column_name: formData.new_column_name }); 
      toast.success('Saved!'); 
      setFormData({ ...formData, new_column_name: '' }); 
    }
    catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleExport = async () => {
    try {
      const customTitles = {
        title: localStorage.getItem('mi_chart_title') || chartTitle,
        xlabel: localStorage.getItem('mi_chart_xlabel') || chartXLabel,
        ylabel: localStorage.getItem('mi_chart_ylabel') || chartYLabel
      };
      const res = await api.post('/api/missing-imputation/export-with-titles', customTitles, { responseType: 'blob' })
        .catch(() => api.get('/api/missing-imputation/export', { responseType: 'blob' }));
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); 
      a.href = url; 
      a.download = 'missing_imputation.xlsx'; 
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export started');
    } catch { toast.error('Export failed'); }
  };

  const handleSaveAnalysis = async () => {
    if (!selectedDataset) {
      toast.error('No dataset selected');
      return;
    }
    const projectId = new URLSearchParams(window.location.search).get('project_id');
    if (!projectId) {
      toast.error('No project selected');
      return;
    }
    
    if (!results) {
      toast.error('No results to save. Please run the imputation first.');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        project_id: parseInt(projectId),
        module_name: 'missing_imputation',
        description: analysisDescription,
        input_parameters: {
          formData: {
            Default_Date: formData.Default_Date?.value || '',
            risk_driver: formData.risk_driver?.value || '',
            Target_variable: formData.Target_variable?.value || '',
            Imputation_method: formData.Imputation_method?.value || '',
            constant_value: formData.constant_value,
            mean_median_mode: formData.mean_median_mode,
            n_neighbors: formData.n_neighbors,
            weights: formData.weights
          },
          savedParams: savedParams,
          columns: columns
        },
        input_dataset: {
          dataset_id: selectedDataset.value,
          dataset_name: selectedDataset.label,
          rows: selectedDataset.rows,
          columns: selectedDataset.columns
        },
        input_upload_id: selectedDataset.value,
        output_result: results,
        status: analysisStatus,
        comments: comments
      };
      const res = await api.post('/analyses/', payload);
      toast.success(`Analysis saved with ID: ${res.data.id}`);
      setSavedAnalysisId(res.data.id);
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save analysis');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMetadata = async () => {
    if (!savedAnalysisId) {
      toast.error('No analysis saved yet');
      return;
    }
    setUpdatingMetadata(true);
    try {
      await api.patch(`/analyses/${savedAnalysisId}`, { 
        status: analysisStatus,
        description: analysisDescription
      });
      toast.success('Status and description updated');
    } catch (err) {
      toast.error('Failed to update');
    } finally {
      setUpdatingMetadata(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    const commentObj = {
      id: Date.now(),
      text: newComment,
      created_at: new Date().toISOString(),
      user_id: localStorage.getItem('user_id') || 'user',
      user_email: localStorage.getItem('user_email') || 'Unknown'
    };
    const updatedComments = [...comments, commentObj];
    
    if (savedAnalysisId) {
      setSavingComment(true);
      try {
        await api.patch(`/analyses/${savedAnalysisId}`, { comments: updatedComments });
        setComments(updatedComments);
        setNewComment('');
        toast.success('Comment added');
      } catch (err) {
        toast.error('Failed to add comment');
      } finally {
        setSavingComment(false);
      }
    } else {
      setComments(updatedComments);
      setNewComment('');
      toast.success('Comment added (will be saved when you save the analysis)');
    }
  };

  // ========== AI Assistant handlers ==========
  const handleAIPlan = async () => {
    if (!aiRequest.trim()) {
      toast.error('Please describe what you want to do');
      return;
    }
    setAiLoading(true);
    setAiPlan(null);
    try {
      const res = await api.post('/api/missing-imputation/ai-plan', { request: aiRequest });
      if (res.data.error) {
        toast.error(res.data.error);
      } else {
        setAiPlan(res.data);
        toast.success('AI plan generated! Review and execute.');
      }
    } catch (err) {
      toast.error('Failed to generate plan');
    } finally {
      setAiLoading(false);
    }
  };

    const handleAIExecute = async () => {
      if (!aiPlan || !aiPlan.plan || aiPlan.plan.length === 0) {
        toast.error('No plan to execute');
        return;
      }
      setAiExecuting(true);
      try {
        // First, execute the plan on the backend
        const res = await api.post('/api/missing-imputation/ai-execute', { plan: aiPlan.plan });
        toast.success(res.data.message);
        
        // Get the first step from the plan
        const firstStep = aiPlan.plan[0];
        
        // Map AI action to Imputation method
        let imputationMethod;
        if (firstStep.action === 'fill_mean' || firstStep.action === 'fill_median' || firstStep.action === 'fill_mode') {
          imputationMethod = 'Mean/Median/Mode';
        } else if (firstStep.action === 'fill_constant') {
          imputationMethod = 'Constant';
        } else if (firstStep.action === 'drop_rows') {
          imputationMethod = 'Remove rows';
        } else {
          imputationMethod = 'Mean/Median/Mode';
        }
        
        // Set form data based on the plan
        const newFormData = {
          ...formData,
          risk_driver: { value: firstStep.column, label: firstStep.column },
          Target_variable: { value: firstStep.target || firstStep.column, label: firstStep.target || firstStep.column },
          Imputation_method: { value: imputationMethod, label: imputationMethod },
        };
        
        // Set method-specific values
        if (firstStep.action === 'fill_constant' && firstStep.value) {
          newFormData.constant_value = firstStep.value;
        } else if (firstStep.action === 'fill_mean') {
          newFormData.mean_median_mode = 'Mean';
        } else if (firstStep.action === 'fill_median') {
          newFormData.mean_median_mode = 'Median';
        } else if (firstStep.action === 'fill_mode') {
          newFormData.mean_median_mode = 'Mode';
        }
        
        setFormData(newFormData);
        
        // Auto-save parameters
        await api.post('/api/missing-imputation/step0', {
          Default_Date: newFormData.Default_Date?.value || '',
          risk_driver: newFormData.risk_driver.value,
          Target_variable: newFormData.Target_variable.value,
          Imputation_method: newFormData.Imputation_method.value
        });
        
        setSavedParams({
          Default_Date: newFormData.Default_Date?.value || '',
          risk_driver: newFormData.risk_driver.label,
          Target_variable: newFormData.Target_variable.label,
          Imputation_method: newFormData.Imputation_method.label
        });
        
        // Build payload for step1
        const payload = {};
        if (imputationMethod === 'Constant') payload.constant_value = firstStep.value;
        else if (imputationMethod === 'Mean/Median/Mode') payload.mean_median_mode = newFormData.mean_median_mode;
        
        // Auto-run the imputation
        const imputeRes = await api.post('/api/missing-imputation/step1', payload);
        setResults(imputeRes.data.results);
        setStep(2);
        toast.success(imputeRes.data.message);
        
      } catch (err) {
        toast.error('Failed to execute plan');
      } finally {
        setAiExecuting(false);
      }
    };
    
  const cs = { 
    control: (b) => ({ ...b, minHeight: '36px', fontSize: '13px', borderColor: '#e0e0e0' }), 
    valueContainer: (b) => ({ ...b, padding: '0 8px' }), 
    placeholder: (b) => ({ ...b, fontSize: '13px', color: '#95a5a6' }), 
    menu: (b) => ({ ...b, zIndex: 9999 }), 
    menuPortal: (b) => ({ ...b, zIndex: 9999 }) 
  };
  
  const stepTabs = ['Select', 'Review', 'Results'];
  const gs = { border: '1px solid #e0e0e0', borderRadius: '8px', marginBottom: '16px', overflow: 'hidden' };
  const ghs = { background: '#f5f7fa', padding: '12px 16px', fontWeight: 600, fontSize: '14px', color: '#2c3e50' };
  const gcs = { padding: '16px', background: 'white' };
  const inputStyle = { width: '100%', padding: '10px 12px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px' };
  const tableHeaders = ['Category', 'Obs Before', 'Obs After', '% Before', '% After', 'Avg Target Before', 'Avg Target After'];

  if (isLoadingAnalysis) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>Loading analysis...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '22px', marginBottom: '16px', color: '#2c3e50' }}>Missing Imputation</h1>
      
      {isViewMode && results && (
        <div style={{ background: '#e8f8f5', padding: '8px 16px', borderRadius: '8px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '13px', color: '#1abc9c' }}>👁️ Viewing saved analysis (read-only)</span>
          <button
            onClick={() => {
              const url = new URL(window.location.href);
              url.searchParams.set('mode', 'edit');
              window.location.href = url.toString();
            }}
            style={{ padding: '4px 12px', fontSize: '12px', background: '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Modify
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: '2px', marginBottom: '20px', borderBottom: '1px solid #e0e0e0', paddingBottom: '8px' }}>
        {stepTabs.map((tab, idx) => (
          <button key={idx} 
            onClick={() => { 
              if (idx === 0) setStep(0); 
              else if (idx === 1 && savedParams.Imputation_method) setStep(1); 
              else if (idx === 2 && results) setStep(2); 
            }}
            style={{ 
              padding: '6px 12px', 
              fontSize: '11px', 
              background: step === idx ? '#1abc9c' : 'transparent', 
              color: step === idx ? 'white' : '#7f8c8d', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer' 
            }}>
            {tab}
          </button>
        ))}
      </div>

      {step === 0 && (
        <div>
          <div style={gs}>
            <div style={ghs}>📊 Select Dataset</div>
            <div style={gcs}>
              {selectedDataset && (
                <div style={{ 
                  marginBottom: '16px', 
                  padding: '12px', 
                  background: '#e8f8f5', 
                  borderRadius: '8px',
                  borderLeft: '4px solid #1abc9c'
                }}>
                  <div style={{ fontSize: '13px', color: '#2c3e50', fontWeight: 500 }}>
                    📌 Currently selected dataset:
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#1abc9c', marginTop: '4px' }}>
                    {selectedDataset.label}
                  </div>
                </div>
              )}
              
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <Select 
                    options={datasets} 
                    value={selectedDataset} 
                    onChange={(newValue) => {
                      setSelectedDataset(newValue);
                      setSelectedDatasetId(null);
                      setColumns([]);
                    }} 
                    styles={cs} 
                    menuPortalTarget={document.body} 
                    placeholder="Select a dataset..."
                    isDisabled={isViewMode}
                  />
                </div>
                <button 
                  onClick={handleSelectDataset} 
                  disabled={!selectedDataset || loading || isViewMode}
                  style={{ 
                    padding: '10px 24px', 
                    background: loading || isViewMode ? '#95a5a6' : '#1abc9c', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '6px', 
                    cursor: loading || isViewMode ? 'not-allowed' : 'pointer', 
                    fontSize: '13px' 
                  }}>
                  {loading ? 'Loading...' : 'Load'}
                </button>
              </div>
            </div>
          </div>

          {/* ========== AI ASSISTANT PANEL ========== */}
          {selectedDataset && columns.length > 0 && (
            <div style={{ ...gs, marginTop: '0px', border: '2px solid #8e44ad' }}>
              <div 
                style={{ ...ghs, background: '#f8f4ff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => setShowAI(!showAI)}
              >
                <span style={{ color: '#6c3483' }}>🤖 AI Assistant - Describe what you want</span>
                <span>{showAI ? '−' : '+'}</span>
              </div>
              {showAI && (
                <div style={gcs}>
                  <textarea
                    value={aiRequest}
                    onChange={(e) => setAiRequest(e.target.value)}
                    placeholder='Example: "Fill missing values in LGD_Regulatory with the median, and remove rows where Default_Date is empty"'
                    style={{
                      width: '100%', padding: '12px', fontSize: '13px', border: '1px solid #ddd',
                      borderRadius: '8px', resize: 'vertical', minHeight: '80px', fontFamily: 'inherit',
                      marginBottom: '12px'
                    }}
                    disabled={isViewMode}
                  />
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={handleAIPlan}
                      disabled={!aiRequest.trim() || aiLoading || isViewMode}
                      style={{
                        padding: '8px 20px', fontSize: '13px',
                        background: (!aiRequest.trim() || aiLoading || isViewMode) ? '#95a5a6' : '#8e44ad',
                        color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer'
                      }}
                    >
                      {aiLoading ? '🤔 Thinking...' : '🤖 Generate Plan'}
                    </button>
                    {aiPlan && aiPlan.plan && aiPlan.plan.length > 0 && (
                      <button
                        onClick={handleAIExecute}
                        disabled={aiExecuting || isViewMode}
                        style={{
                          padding: '8px 20px', fontSize: '13px',
                          background: (aiExecuting || isViewMode) ? '#95a5a6' : '#27ae60',
                          color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer'
                        }}
                      >
                        {aiExecuting ? '⚡ Executing...' : '✅ Execute Plan'}
                      </button>
                    )}
                  </div>
                  
                  {aiPlan && aiPlan.plan && (
                    <div style={{ marginTop: '16px', padding: '12px', background: '#f8f4ff', borderRadius: '8px', border: '1px solid #d5c8e8' }}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: '#6c3483' }}>
                        📋 AI Plan: {aiPlan.summary}
                      </Typography>
                      {aiPlan.plan.map((step, i) => (
                        <div key={i} style={{ 
                          padding: '8px 12px', marginBottom: '4px', background: 'white', 
                          borderRadius: '6px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px',
                          border: '1px solid #e8daef', flexWrap: 'wrap'
                        }}>
                          <Chip label={`Step ${i+1}`} size="small" sx={{ bgcolor: '#8e44ad', color: 'white', fontSize: '10px' }} />
                          <span><strong>{step.column}</strong>: {step.action.replace(/_/g, ' ')}</span>
                          {step.value && <Chip label={`Value: ${step.value}`} size="small" variant="outlined" sx={{ fontSize: '10px' }} />}
                          {step.reason && <span style={{ color: '#7f8c8d', fontSize: '11px' }}>— {step.reason}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {aiPlan && aiPlan.error && (
                    <div style={{ marginTop: '12px', padding: '10px', background: '#fde8e8', borderRadius: '6px', color: '#c0392b', fontSize: '13px' }}>
                      {aiPlan.error}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {columns.length > 0 && (
            <div style={gs}>
              <div style={ghs}>⚙️ Parameters</div>
              <div style={gcs}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Default Date (optional)</label>
                    <Select 
                      options={columnOptions} 
                      value={formData.Default_Date} 
                      onChange={v => setFormData({...formData, Default_Date: v})} 
                      isClearable 
                      styles={cs} 
                      menuPortalTarget={document.body} 
                      placeholder="Select date..."
                      isDisabled={isViewMode}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Imputation Method *</label>
                    <Select 
                      options={methodOptions} 
                      value={formData.Imputation_method} 
                      onChange={v => setFormData({...formData, Imputation_method: v})} 
                      styles={cs} 
                      menuPortalTarget={document.body} 
                      placeholder="Select method..."
                      isDisabled={isViewMode}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Risk Driver *</label>
                    <Select 
                      options={columnOptions} 
                      value={formData.risk_driver} 
                      onChange={v => setFormData({...formData, risk_driver: v})} 
                      styles={cs} 
                      menuPortalTarget={document.body} 
                      placeholder="Select risk driver..."
                      isDisabled={isViewMode}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Target Variable *</label>
                    <Select 
                      options={columnOptions} 
                      value={formData.Target_variable} 
                      onChange={v => setFormData({...formData, Target_variable: v})} 
                      styles={cs} 
                      menuPortalTarget={document.body} 
                      placeholder="Select target..."
                      isDisabled={isViewMode}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button 
                    onClick={handleSaveParameters} 
                    disabled={loading || isViewMode}
                    style={{ 
                      padding: '10px 28px', 
                      background: loading || isViewMode ? '#95a5a6' : '#1abc9c', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '6px', 
                      cursor: loading || isViewMode ? 'not-allowed' : 'pointer', 
                      fontSize: '13px' 
                    }}>
                    {loading ? 'Saving...' : 'Save & Continue →'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {step === 1 && (
        <div>
          <div style={gs}>
            <div style={ghs}>📋 Parameters Summary</div>
            <div style={gcs}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '13px' }}>
                <p><strong>Dataset:</strong> {selectedDataset?.label}</p>
                <p><strong>Method:</strong> {savedParams.Imputation_method}</p>
                <p><strong>Risk Driver:</strong> {savedParams.risk_driver}</p>
                <p><strong>Target:</strong> {savedParams.Target_variable}</p>
              </div>
            </div>
          </div>

          {savedParams.Imputation_method === 'Constant' && (
            <div style={gs}>
              <div style={ghs}>🔧 Method Options</div>
              <div style={gcs}>
                <div style={{ maxWidth: 400 }}>
                  <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Constant Value</label>
                  <input 
                    type="text" 
                    value={formData.constant_value} 
                    onChange={e => setFormData({...formData, constant_value: e.target.value})} 
                    placeholder="e.g. 0" 
                    style={inputStyle}
                    disabled={isViewMode}
                  />
                </div>
              </div>
            </div>
          )}

          {savedParams.Imputation_method === 'Mean/Median/Mode' && (
            <div style={gs}>
              <div style={ghs}>🔧 Method Options</div>
              <div style={gcs}>
                <div style={{ maxWidth: 400 }}>
                  <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Statistic</label>
                  <select 
                    value={formData.mean_median_mode} 
                    onChange={e => setFormData({...formData, mean_median_mode: e.target.value})} 
                    style={inputStyle}
                    disabled={isViewMode}
                  >
                    <option value="Mean">Mean</option>
                    <option value="Median">Median</option>
                    <option value="Mode">Mode</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {savedParams.Imputation_method === 'KNN' && (
            <div style={gs}>
              <div style={ghs}>🔧 Method Options</div>
              <div style={gcs}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, maxWidth: 500 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Neighbors (k)</label>
                    <input 
                      type="number" 
                      value={formData.n_neighbors} 
                      onChange={e => setFormData({...formData, n_neighbors: parseInt(e.target.value)})} 
                      min="1" 
                      max="20" 
                      style={inputStyle}
                      disabled={isViewMode}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Weights</label>
                    <select 
                      value={formData.weights} 
                      onChange={e => setFormData({...formData, weights: e.target.value})} 
                      style={inputStyle}
                      disabled={isViewMode}
                    >
                      <option value="uniform">Uniform</option>
                      <option value="distance">Distance</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button 
              onClick={() => setStep(0)} 
              disabled={isViewMode}
              style={{ padding: '8px 20px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
              ← Back
            </button>
            <button 
              onClick={handleRunImputation} 
              disabled={loading || isViewMode}
              style={{ 
                padding: '10px 24px', 
                background: loading || isViewMode ? '#95a5a6' : '#3498db', 
                color: 'white', 
                border: 'none', 
                borderRadius: 6, 
                cursor: loading || isViewMode ? 'not-allowed' : 'pointer', 
                fontSize: 13 
              }}>
              {loading ? 'Running...' : '🚀 Run Imputation'}
            </button>
          </div>
        </div>
      )}

      {step === 2 && results && (
        <div>
          <div style={{ marginBottom: '20px', background: 'white', borderRadius: '8px', padding: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 500 }}>{chartTitle}</h4>
              <button 
                onClick={() => setShowChartEditor(!showChartEditor)} 
                disabled={isViewMode}
                style={{ padding: '2px 8px', fontSize: '11px', background: '#f0f0f0', border: '1px solid #ddd', borderRadius: '4px', cursor: isViewMode ? 'not-allowed' : 'pointer' }}>
                ✏️ Edit
              </button>
            </div>
            {showChartEditor && (
              <div style={{ display: 'flex', gap: '8px', padding: '8px', background: '#f9f9f9', borderRadius: '4px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <input placeholder="Title" value={chartTitle} onChange={e => setChartTitle(e.target.value)} style={{ flex: 2, fontSize: '12px', padding: '4px' }} />
                <input placeholder="X axis" value={chartXLabel} onChange={e => setChartXLabel(e.target.value)} style={{ flex: 1, fontSize: '12px', padding: '4px' }} />
                <input placeholder="Y axis" value={chartYLabel} onChange={e => setChartYLabel(e.target.value)} style={{ flex: 1, fontSize: '12px', padding: '4px' }} />
                <button onClick={() => { localStorage.setItem('mi_chart_title', chartTitle); localStorage.setItem('mi_chart_xlabel', chartXLabel); localStorage.setItem('mi_chart_ylabel', chartYLabel); setShowChartEditor(false); }}
                  style={{ background: '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 12px', cursor: 'pointer', fontSize: '10px' }}>Apply</button>
              </div>
            )}
            <InteractivePlot plotData={results.plot_json} riskDriver={savedParams.risk_driver} targetVar={savedParams.Target_variable} title={chartTitle} xLabel={chartXLabel} yLabel={chartYLabel} />
          </div>

          <div style={gs}>
            <div style={ghs}>📊 Before/After Summary</div>
            <div style={gcs}>
              <div style={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#2c3e50', position: 'sticky', top: 0, zIndex: 1 }}>
                      {tableHeaders.map(h => (<th key={h} style={{ color: 'white', fontWeight: 600, padding: '8px 12px', textAlign: 'left', fontSize: '11px' }}>{h}</th>))}
                    </tr>
                  </thead>
                  <tbody>
                    {results.data?.map((row, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #eee', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                        <td style={{ padding: '6px 12px', fontFamily: 'monospace' }}>{row.Category}</td>
                        <td style={{ padding: '6px 12px' }}>{row.obs_before?.toLocaleString()}</td>
                        <td style={{ padding: '6px 12px' }}>{row.obs_after?.toLocaleString()}</td>
                        <td style={{ padding: '6px 12px' }}>{row.pct_before?.toFixed(2)}%</td>
                        <td style={{ padding: '6px 12px' }}>{row.pct_after?.toFixed(2)}%</td>
                        <td style={{ padding: '6px 12px' }}>{row.avg_target_before?.toFixed(6)}</td>
                        <td style={{ padding: '6px 12px' }}>{row.avg_target_after?.toFixed(6)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Comments Section */}
          <div style={{ ...gs, marginTop: '20px' }}>
            <div 
              style={{ 
                ...ghs, 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                cursor: 'pointer',
                userSelect: 'none'
              }}
              onClick={() => setIsCommentsExpanded(!isCommentsExpanded)}
            >
              <span>💬 Comments & Discussions ({comments.length})</span>
              <span style={{ fontSize: '20px', fontWeight: 'bold' }}>
                {isCommentsExpanded ? '−' : '+'}
              </span>
            </div>
            
            {isCommentsExpanded && (
              <div style={gcs}>
                <div style={{ maxHeight: '300px', overflow: 'auto', marginBottom: '16px' }}>
                  {comments.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                      No comments yet. Be the first to add a comment.
                    </Typography>
                  ) : (
                    comments.map((comment) => (
                      <div
                        key={comment.id}
                        style={{
                          padding: '10px 12px',
                          marginBottom: '8px',
                          background: '#f8f9fa',
                          borderRadius: '8px',
                          borderLeft: '3px solid #1abc9c'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                          <span style={{ fontSize: '11px', fontWeight: 600, color: '#2c3e50' }}>
                            {comment.user_email || comment.user_id || 'Unknown'}
                          </span>
                          <span style={{ fontSize: '10px', color: '#95a5a6' }}>
                            {new Date(comment.created_at).toLocaleString()}
                          </span>
                        </div>
                        <div style={{ fontSize: '13px', color: '#34495e', whiteSpace: 'pre-wrap' }}>
                          {comment.text}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment or question..."
                    style={{
                      flex: 1,
                      padding: '10px',
                      fontSize: '13px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      resize: 'vertical',
                      minHeight: '60px',
                      fontFamily: 'inherit'
                    }}
                  />
                  <button
                    onClick={handleAddComment}
                    disabled={savingComment || !newComment.trim()}
                    style={{
                      padding: '8px 20px',
                      background: (savingComment || !newComment.trim()) ? '#95a5a6' : '#1abc9c',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: (savingComment || !newComment.trim()) ? 'not-allowed' : 'pointer',
                      fontSize: '13px',
                      height: 'fit-content'
                    }}
                  >
                    {savingComment ? 'Sending...' : 'Add Comment'}
                  </button>
                </div>
                
                {!savedAnalysisId && comments.length > 0 && (
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                    💡 Comments will be permanently saved when you click "Save Analysis"
                  </Typography>
                )}
              </div>
            )}
          </div>

          {/* Professional Action Area */}
          <div style={{ marginTop: '24px', padding: '20px', background: '#f8f9fa', borderRadius: '12px', border: '1px solid #e0e0e0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 500, display: 'block', marginBottom: 4 }}>New Column Name</label>
                  <input 
                    type="text" 
                    value={formData.new_column_name} 
                    onChange={e => setFormData({...formData, new_column_name: e.target.value})} 
                    placeholder="imputed_col" 
                    style={{ padding: '8px 12px', fontSize: 12, border: '1px solid #ddd', borderRadius: 6, width: 160 }}
                    disabled={isViewMode}
                  />
                </div>
                <button 
                  onClick={handleSaveColumn} 
                  disabled={saving || isViewMode}
                  style={{ 
                    padding: '8px 20px', 
                    background: saving || isViewMode ? '#95a5a6' : '#27ae60', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: 6, 
                    cursor: saving || isViewMode ? 'not-allowed' : 'pointer', 
                    fontSize: 13, 
                    whiteSpace: 'nowrap',
                    marginTop: '18px'
                  }}>
                  {saving ? 'Saving...' : '💾 Save Column'}
                </button>
              </div>
              
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <MuiSelect
                  value={analysisStatus}
                  onChange={(e) => setAnalysisStatus(e.target.value)}
                  label="Status"
                  disabled={isViewMode}
                >
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="validated">Validated</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </MuiSelect>
              </FormControl>
            </div>
            
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={analysisDescription}
              onChange={(e) => setAnalysisDescription(e.target.value)}
              placeholder="Describe the purpose, methodology, or key findings of this imputation..."
              disabled={isViewMode}
              sx={{ mb: 3 }}
            />
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                onClick={() => setStep(1)}
                disabled={isViewMode}
                size="small"
              >
                ← Back
              </Button>
              
              <Button
                variant="outlined"
                onClick={() => { 
                  setStep(0); 
                  setSelectedDataset(null); 
                  setSelectedDatasetId(null);
                  setColumns([]);
                  setSavedParams({});
                  setResults(null); 
                  setAnalysisStatus('draft'); 
                  setAnalysisDescription(''); 
                  setSavedAnalysisId(null); 
                  setComments([]);
                  setAiRequest('');
                  setAiPlan(null);
                  setFormData({
                    Default_Date: null, risk_driver: null, Target_variable: null, Imputation_method: null,
                    constant_value: '', mean_median_mode: 'Mean', n_neighbors: 2, weights: 'uniform', new_column_name: ''
                  });
                }}
                disabled={isViewMode}
                size="small"
              >
                New Analysis
              </Button>
              
              {isEditMode && savedAnalysisId && (
                <Button
                  variant="outlined"
                  onClick={handleUpdateMetadata}
                  disabled={updatingMetadata}
                  size="small"
                >
                  {updatingMetadata ? 'Updating...' : 'Update Metadata'}
                </Button>
              )}
              
              <Button
                variant="contained"
                onClick={handleExport}
                size="small"
                sx={{ bgcolor: '#27ae60', '&:hover': { bgcolor: '#219a52' } }}
              >
                📥 Export
              </Button>
              
              {isEditMode && (
                <Button
                  variant="contained"
                  onClick={handleSaveAnalysis}
                  disabled={!results || loading}
                  size="small"
                  sx={{ bgcolor: results && !loading ? '#1abc9c' : '#95a5a6', '&:hover': { bgcolor: '#16a085' } }}
                >
                  {loading ? 'Saving...' : '💾 Save Analysis'}
                </Button>
              )}
            </Box>
          </div>
        </div>
      )}
    </div>
  );
};

export default MissingImputation;